import { useEffect, useState } from 'react';

import { IconCheckCircleOutline } from '@iwoca/icons';
import { GetBuyerSpendingLimitResponse } from '@iwoca/lapi-client/iwocapay';
import { Button, Spinner } from '@iwoca/orion';

import { displayCurrency } from '../../../../../utils/DisplayCurrency';
import { WhiteLabelOverride } from '../../../../components/WhiteLabelOverride/WhiteLabelOverride';
import { useEcomPostMessage } from '../../../../hooks/useEcomPostMessage';
import { useCurrentPayLink } from '../../../hooks/useCurrentPayLink';
import { DISPLAY_TYPE_ENUM, TDisplayType } from '../DrawDownModal';
import { SpendingLimitBar } from '../SpendingLimitBar/SpendingLimitBar';

type TSpendingLimit =
  GetBuyerSpendingLimitResponse['data']['active_spending_limit'];

export const ECOM_REDIRECT_DELAY_IN_SECONDS = 5;

export const Success = ({
  finishDrawDown,
  displayType,
  redirectUrl,
  spendingLimit,
}: {
  finishDrawDown: () => void;
  displayType: TDisplayType;
  redirectUrl: string;
  spendingLimit?: TSpendingLimit;
}) => {
  const { payLink } = useCurrentPayLink();
  const [redirectCount, setRedirectCount] = useState(
    ECOM_REDIRECT_DELAY_IN_SECONDS,
  );
  const [showFooter, setShowFooter] = useState(false);

  useEffect(() => {
    if (showFooter) return;

    const footerTimeout = setTimeout(() => {
      setShowFooter(true);
    }, 1500);

    return () => clearTimeout(footerTimeout);
  }, [showFooter]);

  useEffect(() => {
    if (window.IS_STORYBOOK) return;
    if (displayType !== DISPLAY_TYPE_ENUM.ECOMMERCE) return;
    if (!showFooter) return;

    if (redirectCount === 0) {
      window.location.href = redirectUrl;
      return;
    }

    const redirectTimout = setTimeout(() => {
      setRedirectCount((prevCount) => prevCount - 1);
    }, 1000);
    return () => clearTimeout(redirectTimout);
  }, [showFooter, redirectCount, redirectUrl, displayType]);

  useEcomPostMessage({
    enabled: displayType === DISPLAY_TYPE_ENUM.OVERLAY,
    status: 'SUCCESSFUL',
    redirectUrl,
  });

  const getFooter = () => {
    return (
      <div
        className="my-2xl mb-l flex animate-fade-in-animation items-center justify-center text-s font-med text-structure-40 opacity-0"
        data-ocularid="drawdown-success-footer"
      >
        {displayType === DISPLAY_TYPE_ENUM.OVERLAY && (
          <>
            You can now safely close this window and return to{' '}
            {payLink?.seller_trading_name}.
          </>
        )}
        {displayType === DISPLAY_TYPE_ENUM.ECOMMERCE && (
          <>
            <Spinner className="mr-s" dataTestId="drawdown-reload-spinner" />{' '}
            Taking you back to {payLink?.seller_trading_name}... {redirectCount}
            s
          </>
        )}
        {displayType === DISPLAY_TYPE_ENUM.DEFAULT && (
          <WhiteLabelOverride componentType="button" disabled={false}>
            <Button variant="primary" onClick={finishDrawDown}>
              Go to the dashboard
            </Button>
          </WhiteLabelOverride>
        )}
      </div>
    );
  };

  if (!payLink || !spendingLimit) return null;

  // Calculate the new spent amount on the FE so we don't have to wait for the BE to update
  const updatedSpentAmount = spendingLimit.spent_amount + payLink.amount;
  const updatedPercentageSpent =
    (updatedSpentAmount / spendingLimit.max_amount) * 100;

  return (
    <div className="flex h-full flex-col justify-between">
      <div>
        <div className="flex flex-col items-center gap-s">
          <IconCheckCircleOutline className="h-[3rem] w-[3rem] fill-success-mid" />
          <div className="text-3xl font-bold">Payment complete</div>
          <div className="text-xl font-reg">
            We've sent the money to {payLink?.seller_trading_name}
          </div>
        </div>
        <div className="mt-2xl flex justify-between text-xs font-reg">
          <div className="flex flex-col items-start">
            <div>Total spent:</div>
            <div className="text-l font-med">
              {displayCurrency(updatedSpentAmount)}
            </div>
          </div>
          <div className="flex flex-col items-end">
            <div>Total spending limit:</div>
            <div className="text-l font-med">
              {displayCurrency(spendingLimit.max_amount)}
            </div>
          </div>
        </div>
        <SpendingLimitBar
          newPercentageSpent={updatedPercentageSpent}
          prevPercentageSpent={spendingLimit.spent_amount_percentage}
          animationDelayInMs={500}
        />
      </div>
      {showFooter && getFooter()}
    </div>
  );
};
